import axios from "axios";
import { API_URL } from "../utils/api-config";

// Types
export interface Vehicle {
  _id: string;
  plate: string;
  brand: string;
  model: string;
  type?: string;
  year?: number;
  color?: string;
  mileage?: number;
  fuelLevel?: number;
  status?: "disponible" | "en_mission" | "en_maintenance";
}

export interface DamagePosition {
  x: number;
  y: number;
}

export interface Damage {
  position: DamagePosition;
  type: "rayure" | "choc" | "manquant" | "casse";
  face:
    | "avant"
    | "arriere"
    | "coteConducteur"
    | "cotePassager"
    | "toit"
    | "gauche"
    | "droite";
  driverId: string;
  driverName: string;
  description?: string;
  images?: string[];
  cost?: number;
  isRepaired?: boolean;
  repairedAt?: string;
  createdAt: string;
}

export interface VehicleHistory {
  assignments: {
    _id: string;
    vehiclePlate: string;
    driverId: string;
    driverFirstName: string;
    driverLastName: string;
    date: string;
    time: string;
    status: "en_cours" | "terminée";
    currentStep: string;
    purpose: string;
    startMileage: number;
    startFuelLevel: number | string;
    endMileage?: number;
    endFuelLevel?: number | string;
    comments?: string;
    initialState?: {
      cleanliness: {
        interior: boolean;
        exterior: boolean;
        cabine: boolean;
        comments?: string;
      };
      checklist: {
        lights: "conforme" | "non_conforme";
        brakes: "conforme" | "non_conforme";
        tires: "conforme" | "non_conforme";
        documents: "conforme" | "non_conforme";
        comments?: string;
      };
      degradations: {
        details: {
          rayures: number;
          chocs: number;
          manquant: number;
          casse: number;
        };
        count: number;
        images: {
          position: DamagePosition;
          face: string;
          damageType: string;
          description?: string;
          driverId: string;
          _id: string;
        }[];
      };
    };
    finalState?: {
      cleanliness: {
        interior: boolean;
        exterior: boolean;
        cabine: boolean;
        comments?: string;
      };
      checklist: {
        lights: "conforme" | "non_conforme";
        brakes: "conforme" | "non_conforme";
        tires: "conforme" | "non_conforme";
        documents: "conforme" | "non_conforme";
        comments?: string;
      };
      degradations: {
        details: {
          rayures: number;
          chocs: number;
          manquant: number;
          casse: number;
        };
        count: number;
        images: {
          position: DamagePosition;
          face: string;
          damageType: string;
          description?: string;
          driverId: string;
          _id: string;
        }[];
      };
    };
    damages: Array<Damage>;
  }[];
  damagesByFace: {
    avant: Damage[];
    arriere: Damage[];
    coteConducteur: Damage[];
    cotePassager: Damage[];
    toit: Damage[];
    gauche?: Damage[];
    droite?: Damage[];
  };
  allDamages?: Damage[]; // Nouvelle propriété
  faceImages: {
    avant: string;
    arriere: string;
    coteConducteur: string;
    cotePassager: string;
    toit: string;
    gauche: string;
    droite: string;
    interieur?: string;
    moteur?: string;
  };
  vehicleInfo?: {
    plate: string;
    brand: string;
    vehicleModel?: string;
    year?: string;
    mileage?: string;
    fuelType?: string;
    vehicleType?: string;
  };
  inspectionPhotos?: {
    front?: string;
    back?: string;
    driverSide?: string;
    passengerSide?: string;
    interior?: string;
    engine?: string;
    damage?: string;
  };
}

// Nouvelle interface pour les dégâts historiques
export interface HistoricalDamage {
  position: DamagePosition;
  face: string;
  damageType: string;
  driverId: string;
  url?: string;
  _id: string;
  assignmentId?: string;
  assignmentDate?: string;
  isPreexisting?: boolean;
  isInitial?: boolean;
  isNew?: boolean;
  phase?: string;
  description?: string;
}

// Interface pour la réponse de l'API d'historique des dégâts
export interface AssignmentWithHistoricalDamages {
  _id: string;
  vehiclePlate: string;
  driverId: string;
  driverFirstName: string;
  driverLastName: string;
  date: string;
  time: string;
  status: "en_cours" | "terminée";
  // ... autres propriétés standard d'une affectation

  historicalDamages: {
    previous: Record<string, HistoricalDamage[]>;
    initial: Record<string, HistoricalDamage[]>;
    new: Record<string, HistoricalDamage[]>;
    combined: Record<string, HistoricalDamage[]>;
  };

  faceImages: Record<string, string>;
}

// Service pour gérer les véhicules
const VehicleService = {
  // Récupérer tous les véhicules
  getAll: async (): Promise<Vehicle[]> => {
    try {
      const response = await axios.get(`${API_URL}/vehicles`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des véhicules:", error);
      throw error;
    }
  },

  // Récupérer un véhicule par ID
  getById: async (id: string): Promise<Vehicle> => {
    try {
      const response = await axios.get(`${API_URL}/vehicles/${id}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération du véhicule:", error);
      throw error;
    }
  },

  // Récupérer un véhicule par plaque d'immatriculation
  getByPlate: async (plate: string): Promise<Vehicle> => {
    try {
      const response = await axios.get(`${API_URL}/vehicles/details/${plate}`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération du véhicule:", error);
      throw error;
    }
  },

  // Récupérer l'historique complet d'un véhicule
  getVehicleHistory: async (plate: string): Promise<VehicleHistory> => {
    try {
      console.log(
        `[VehicleService] Récupération de l'historique du véhicule: ${plate}`
      );
      const response = await axios.get(`${API_URL}/vehicles/${plate}/history`);
      console.log(`[VehicleService] Historique récupéré:`, response.data);
      return response.data;
    } catch (error) {
      console.error(
        `[VehicleService] Erreur lors de la récupération de l'historique du véhicule ${plate}:`,
        error
      );
      throw error;
    }
  },

  // Nouvelle méthode : Récupérer l'historique complet des dégâts d'une affectation
  getAssignmentWithHistoricalDamages: async (
    assignmentId: string
  ): Promise<AssignmentWithHistoricalDamages> => {
    try {
      console.log(
        `[VehicleService] Récupération de l'historique des dégâts pour l'affectation: ${assignmentId}`
      );
      const response = await axios.get(
        `${API_URL}/assignments/${assignmentId}/with-historical-damages`
      );
      console.log(
        `[VehicleService] Historique des dégâts récupéré:`,
        response.data
      );
      return response.data;
    } catch (error) {
      console.error(
        `[VehicleService] Erreur lors de la récupération de l'historique des dégâts pour l'affectation ${assignmentId}:`,
        error
      );
      throw error;
    }
  },
};

export default VehicleService;
